// ============================================================
// ByOurConsent.com -- Premium Subscribe Page
// /premium/subscribe
// Confirmation page + card entry for 30-day free trial
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// ============================================================
// Checkout form
// ============================================================
const SubscribeForm = ({ isReturning }) => {
    const stripe    = useStripe();
    const elements  = useElements();
    const navigate  = useNavigate();
    const { user }  = useAuth();
    const [loading,   setLoading]   = useState(false);
    const [agreed,    setAgreed]    = useState(false);
    const [formError, setFormError] = useState('');

    const trialEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    const handleSubmit = async () => {
        if (!stripe || !elements || !agreed) return;
        setLoading(true);
        setFormError('');

        try {
            const cardElement = elements.getElement(CardElement);
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    email: user.email,
                    name:  `${user.first_name} ${user.last_name}`
                }
            });

            if (error) {
                setFormError(error.message);
                setLoading(false);
                return;
            }

            const endpoint = isReturning ? '/subscriptions/subscribe' : '/subscriptions/start-trial';
            const response = await api.post(endpoint, { payment_method_id: paymentMethod.id });

            if (response.data.success) {
                toast.success(isReturning ? 'Welcome back! Membership activated.' : 'Your 30-day free trial has started!');
                navigate('/premium');
            }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed. Please try again.';
            setFormError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '540px', margin: '0 auto', padding: '2rem 1.5rem' }}>

            {/* What you're signing up for */}
            <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem' }}>
                    {isReturning ? 'Reactivate your membership' : 'What you\'re signing up for'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
                    {(isReturning ? [
                        { label: 'Monthly rate', value: '$9 / month' },
                        { label: 'First charge', value: 'Today' },
                        { label: 'Cancel anytime', value: 'No penalty' },
                        { label: 'Access', value: 'Immediate' },
                    ] : [
                        { label: 'Free trial', value: '30 days' },
                        { label: 'After trial', value: '$9 / month' },
                        { label: 'Trial ends', value: trialEndDate },
                        { label: 'Cancel anytime', value: 'No penalty' },
                    ]).map(({ label, value }) => (
                        <div key={label} style={{ padding: '10px 12px', background: '#ede8dc', borderRadius: '3px' }}>
                            <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
                            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '2px' }}>{value}</div>
                        </div>
                    ))}
                </div>
                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                    {isReturning
                        ? 'Your card will be charged $9 today. Your membership renews monthly until cancelled.'
                        : `Your card will not be charged during the 30-day trial. After the trial ends, your membership automatically continues at $9/month unless you cancel before ${trialEndDate}.`}
                </div>
            </div>

            {/* What's included */}
            <div style={{ background: '#1e2a3a', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '10px' }}>
                    Full access to all 9 premium tools
                </div>
                {[
                    '📋 FOIA & Public Records Generator',
                    '📜 Constitutional Notice Generator',
                    '🏛️ AI Bill Analysis',
                    '⚖️ Constitutional Research Assistant',
                    '🛡️ Your Rights Analyzer',
                    '📰 Law vs Action Checker',
                    '🔎 Elected Official Accountability',
                    '💰 Follow the Money',
                    '📡 Official Tracker',
                ].map(tool => (
                    <div key={tool} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa', padding: '3px 0', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
                        {tool}
                    </div>
                ))}
            </div>

            {/* Card input */}
            <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Credit or debit card
                </label>
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '12px' }}>
                    <CardElement options={{
                        style: {
                            base: {
                                fontSize:   '14px',
                                color:      '#1e2a3a',
                                fontFamily: 'sans-serif',
                                '::placeholder': { color: '#8a8070' }
                            },
                            invalid: { color: '#8b1a1a' }
                        },
                        hidePostalCode: false
                    }} />
                </div>
                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '4px' }}>
                    🔒 Secured by Stripe. {isReturning ? 'Your card will be charged $9 today.' : 'Your card will not be charged until after your 30-day trial.'}
                </div>
            </div>

            {/* Agreement */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '1.5rem', cursor: 'pointer', padding: '10px 12px', background: '#f5f0e8', border: `0.5px solid ${agreed ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px' }}>
                <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.6, flex: 1 }}>
                    {isReturning
                        ? 'I understand my card will be charged $9 today and monthly thereafter. I can cancel anytime from my account page.'
                        : 'I understand that after my 30-day free trial, my membership will automatically renew at $9/month and my card on file will be charged. I can cancel anytime from my account page before the trial ends.'}
                </span>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, marginTop: '2px' }} />
            </label>

            {formError && (
                <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 14px', marginBottom: '1rem', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a' }}>
                    {formError}
                </div>
            )}

            <button onClick={handleSubmit} disabled={!stripe || !agreed || loading}
                style={{ width: '100%', background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '14px', fontSize: '14px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer', opacity: (!stripe || !agreed || loading) ? 0.6 : 1 }}>
                {loading
                    ? (isReturning ? 'Activating...' : 'Starting your trial...')
                    : (isReturning ? 'Activate membership — $9/month →' : 'Start my 30-day free trial →')}
            </button>

            <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                {isReturning
                    ? 'Charged $9 today · Cancel anytime'
                    : `No charge today · Cancel before ${trialEndDate} to avoid being charged`}
            </div>
        </div>
    );
};

// ============================================================
// Main page
// ============================================================
// Pre-launch heads-up banner -- informational only, never blocks
// anything. Toggle off from Admin > Settings once ready for real launch.
const PrelaunchBanner = ({ settings }) => {
    if (settings.prelaunch_banner_enabled !== 'true') return null;
    return (
        <div style={{
            background: '#5a3a1a', color: '#f5f0e8', padding: '10px 20px',
            fontSize: '12px', fontFamily: 'sans-serif', textAlign: 'center', lineHeight: 1.5
        }}>
            {settings.prelaunch_banner_text ||
                "We're finishing up! Orders placed now are part of final testing."}
        </div>
    );
};

const PremiumSubscribePage = () => {
    const { user }  = useAuth();
    const navigate  = useNavigate();
    const [checking,    setChecking]    = useState(true);
    const [isReturning, setIsReturning] = useState(false);
    const [settings,    setSettings]    = useState({});

    useEffect(() => {
        api.get('/admin/settings/public')
            .then(res => setSettings(res.data.settings || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        api.get('/subscriptions/status')
            .then(r => {
                if (r.data.isActive) {
                    navigate('/premium');
                } else if (r.data.status !== 'none') {
                    // Had a subscription before but not active -- returning user
                    setIsReturning(true);
                }
            })
            .catch(() => {})
            .finally(() => setChecking(false));
    }, [navigate]);

    if (checking) return (
        <div style={{ minHeight: '100vh', background: '#f5f0e8' }}>
            <Nav />
            <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>
                Checking your account...
            </div>
        </div>
    );

    if (!user) {
        return (
            <div style={{ minHeight: '100vh', background: '#f5f0e8' }}>
                <Nav />
                <PrelaunchBanner settings={settings} />
                <div style={{ maxWidth: '540px', margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem' }}>
                        Create a free account first
                    </div>
                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1.5rem' }}>
                        You need a free account before starting your premium trial.
                    </div>
                    <Link to="/register" style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                        Create free account →
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <Helmet><title>Start Free Trial — By Our Consent Premium</title></Helmet>
            <Nav />
            <PrelaunchBanner settings={settings} />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '540px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium membership
                    </Link>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '10px' }}>
                        {isReturning ? 'Reactivate your membership' : 'Start your 30-day free trial'}
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        {isReturning ? 'Welcome back. Subscribe at $9/month for full access to all 9 civic tools.' : 'Full access to all 9 premium civic tools. No charge for 30 days. Cancel anytime before the trial ends and you won\'t be charged.'}
                    </p>
                </div>
            </div>

            <Elements stripe={stripePromise} options={{
                appearance: {
                    theme: 'stripe',
                    variables: {
                        colorPrimary:    '#8b1a1a',
                        colorBackground: '#f5f0e8',
                        colorText:       '#1e2a3a',
                        borderRadius:    '3px'
                    }
                }
            }}>
                <SubscribeForm isReturning={isReturning} />
            </Elements>

            <Footer />
        </>
    );
};

export default PremiumSubscribePage;
